import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { OverviewSectionChartConfig } from '@components/pages/Dashboard/Overview/OverviewMetricSectionCardContent.component'
import { OVERVIEW_PROGRESS_CHART_TITLES } from '@constants/Dashboard/overviewDashboard.constants'
import {
  formatChartCurrencyKUnits,
  HYGIENE_PLACEHOLDER,
  toGoalNumber,
} from '@utils/dashboardFinance.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { getProductionOverview } from '@services/api/dashboard/finance.api'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
} from './useDashboardPeriod'
import { useClinicGoal } from './useClinicGoal'

type UseProductionOverviewDetailOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type ProductionOverviewSlice = {
  name: string
  value: number
  color: string
}

export type UseProductionOverviewDetailResult = {
  loading: boolean
  error: string | null
  hasData: boolean
  /** True once production data exists and the clinic goal request has settled. */
  hasPayload: boolean
  topMetrics: DashboardMetricItemData[]
  highlight: {
    leftLabel: string
    grossActual: string
    grossGoal: string
  }
  chart: OverviewSectionChartConfig
  /** Undefined so DashboardHalfDonutChart uses the gross-vs-goal fallback ring. */
  grossSlices: ProductionOverviewSlice[] | undefined
  insuranceSlices: ProductionOverviewSlice[] | undefined
  valueFormatter: (v: number) => string
  grossActual: number
  grossGoal: number
  insValue: number
  patientValue: number
}

function formatDollarsCompactForKpi(dollars: number): string {
  return formatChartCurrencyKUnits(dollars / 1000)
}

/**
 * Overview production card: production + clinic goals only.
 * Skips department/collection fan-out used by the full production detail page.
 */
export function useProductionOverviewDetail({
  range,
  date,
  enabled = true,
  queued = false,
}: UseProductionOverviewDetailOptions): UseProductionOverviewDetailResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const productionState = useDashboardPeriodFetch(
    queryParams,
    getProductionOverview,
    {
      enabled,
      queued,
      cacheKey: 'getProductionOverview',
    },
  )
  const { goal: clinicGoal, loading: goalLoading } = useClinicGoal({
    range,
    date,
    enabled,
    queued,
  })

  const loading = productionState.loading || (enabled && goalLoading)
  const error = productionState.error

  const grossDollars =
    productionState.data?.summary.grossProduction.current ?? 0
  const netDollars = productionState.data?.summary.netProduction.current ?? 0
  const adjPct = productionState.data?.summary.adjustmentPercentage.current ?? 0
  const adjustments = (adjPct / 100) * netDollars

  const grossK = grossDollars / 1000
  const grossGoalK = toGoalNumber(clinicGoal?.gross_production_goal) / 1000

  const topMetrics = useMemo((): DashboardMetricItemData[] => {
    if (productionState.data == null) {
      return [
        {
          id: 'gross-prod',
          leftLabel: 'Gross production',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'net-prod',
          leftLabel: 'Net production',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'adjustments',
          leftLabel: 'Adjustments',
          value: HYGIENE_PLACEHOLDER,
        },
      ]
    }
    return [
      {
        id: 'gross-prod',
        leftLabel: 'Gross production',
        value: formatDollarsCompactForKpi(grossDollars),
      },
      {
        id: 'net-prod',
        leftLabel: 'Net production',
        value: formatDollarsCompactForKpi(netDollars),
      },
      {
        id: 'adjustments',
        leftLabel: 'Adjustments',
        value: formatDollarsCompactForKpi(adjustments),
      },
    ]
  }, [productionState.data, grossDollars, netDollars, adjustments])

  // Department/insurance splits require extra endpoints; overview card shows
  // the gross-vs-goal ring. Pass undefined (not []) so the half-donut fallback runs.
  const grossSlices = undefined
  const insuranceSlices = undefined

  const highlight = useMemo(
    () => ({
      leftLabel: 'Gross production',
      grossActual: formatChartCurrencyKUnits(grossK),
      grossGoal: formatChartCurrencyKUnits(grossGoalK),
    }),
    [grossK, grossGoalK],
  )

  const chart = useMemo(
    (): OverviewSectionChartConfig => ({
      title: OVERVIEW_PROGRESS_CHART_TITLES[range],
      primaryValue: grossK,
      secondaryValue: grossGoalK,
      valueFormatter: formatChartCurrencyKUnits,
      // Omit slices so DashboardHalfDonutChart builds the gross-vs-goal ring.
    }),
    [grossK, grossGoalK, range],
  )

  // Clinic goal is a target, not production activity; only response-backed
  // production values count as meaningful data.
  const hasData = hasNonZeroDashboardValues(grossDollars, netDollars)
  const goalSettled = clinicGoal != null || !goalLoading
  const hasPayload = productionState.data != null && goalSettled

  return {
    loading,
    error,
    hasData,
    hasPayload,
    topMetrics,
    highlight,
    chart,
    grossSlices,
    insuranceSlices,
    valueFormatter: formatChartCurrencyKUnits,
    grossActual: grossK,
    grossGoal: grossGoalK,
    insValue: 0,
    patientValue: 0,
  }
}
